import { createColumn, formatDateTime } from "../../utils/helpers";
import { TFieldConfig } from "../../types/common";
import { API_DISTRICTS, API_INVOICES_PAID_ALL } from "../../utils/apis";
import { useEffect, useState } from "react";
import useGet from "../common/useGet";
import { PaymentStatusTag } from "../../components/common/registerUtils";

interface UseVehicleLedgerInvoicesProps {
  form: any;
  updateFormValue: (field: string, value: any) => void;
  updateFormValues: (values: any) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  filters: any;
  setFilters: (filters: any) => void;
  setError: (error: any) => void;
}

export const useVehicleLedgerInvoices = ({}: UseVehicleLedgerInvoicesProps) => {
  const { loadData: getDistricts, data: districts } = useGet();
  const [selectedInvoices, setSelectedInvoices] = useState<any[]>([]);

  useEffect(() => {
    getDistricts({ api: API_DISTRICTS });
  }, []);

  const districtOptions =
    districts?.map((district: any) => ({
      value: district.name,
      label: district.name,
    })) || [];

  const searchFields: TFieldConfig[] = [
    {
      field_name: "car_number",
      label: "Car Number",
      type: "text",
      props: {
        placeholder: "Enter car number",
        required: true,
      },
    },
    {
      field_name: "district_name",
      type: "select",
      props: {
        options: districtOptions,
        placeholder: "Select district",
      },
    },
    {
      field_name: "start_date",
      label: "Start Date",
      type: "date",
      props: {
        placeholder: "DD/MM/YYYY",
        format: "DD/MM/YYYY",
      },
    },
    {
      field_name: "end_date",
      label: "End Date",
      type: "date",
      props: {
        placeholder: "DD/MM/YYYY",
        format: "DD/MM/YYYY",
      },
    },
  ];

  const tableData = (data: any) => {
    if (!data) return [];
    const results = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data)
        ? data
        : [];

    return results?.map((item: any, idx: number) => ({
      ...item,
      id: item.id,
      index: idx + 1,
      generated_at: item.generated_at
        ? formatDateTime(item.generated_at)
        : "N/A",
      invoice_number: item.invoice_number || "N/A",
      total_due: item.total_due ? item.total_due : "0",
      payment_status: <PaymentStatusTag status={item.payment_status} />,
    }));
  };

  const handleCheckboxChange = (invoiceId: any, invoice: any) => {
    setSelectedInvoices((prev) => {
      const isSelected = prev.some((item) => item.id === invoiceId);

      if (isSelected) {
        return prev.filter((item) => item.id !== invoiceId);
      } else {
        return [...prev, invoice];
      }
    });
  };

  // Header checkbox: adds every row on the current page, or clears them if the
  // whole page is already selected. Rows from other pages are left untouched,
  // so ticks accumulate as you paginate.
  const toggleSelectPage = (pageRows: any[]) => {
    setSelectedInvoices((prev) => {
      const selectedIds = new Set(prev.map((item) => item.id));
      const wholePageSelected =
        pageRows.length > 0 && pageRows.every((row) => selectedIds.has(row.id));

      if (wholePageSelected) {
        const pageIds = new Set(pageRows.map((row) => row.id));
        return prev.filter((item) => !pageIds.has(item.id));
      }

      return [...prev, ...pageRows.filter((row) => !selectedIds.has(row.id))];
    });
  };

  const tableColumns = (rows?: any) => {
    // The table stores selections as mapped rows, so map here too and compare by id.
    const mapped = tableData(rows);
    const pageRows = Array.isArray(mapped) ? mapped : [];
    const selectedIds = new Set(selectedInvoices.map((item) => item.id));
    const selectedOnPage = pageRows.filter((row: any) =>
      selectedIds.has(row.id),
    ).length;

    return [
      {
        id: "select",
        header: (
          <input
            type="checkbox"
            aria-label="Select all invoices on this page"
            title="Select all on this page"
            checked={pageRows.length > 0 && selectedOnPage === pageRows.length}
            ref={(el: HTMLInputElement | null) => {
              if (el) {
                el.indeterminate =
                  selectedOnPage > 0 && selectedOnPage < pageRows.length;
              }
            }}
            onChange={() => toggleSelectPage(pageRows)}
          />
        ),
        sortable: false,
        render: (item: any) => (
          <input
            type="checkbox"
            aria-label={`Select invoice ${item.invoice_number || ""}`}
            checked={selectedInvoices.some((invoice) => invoice.id === item.id)}
            onChange={(e) => {
              e.stopPropagation();
              handleCheckboxChange(item.id, item);
            }}
          />
        ),
      },
      createColumn("generated_at", false, "Date"),
      createColumn("invoice_number", false, "Invoice No."),
      createColumn("total_due", false, "Total Due"),
      createColumn("district_name", false, "District"),
      createColumn("created_by", false, "Created By"),
      createColumn("payment_status", true, "Payment Status"),
    ];
  };

  const api = API_INVOICES_PAID_ALL;
  const tableTitle = "Invoices";
  const initialFilters = {};

  return {
    searchFields,
    tableColumns,
    tableData,
    tableTitle,
    api,
    initialFilters,
    selectedInvoices,
    setSelectedInvoices,
  };
};
